import { Theme } from '@mui/material';

import { tierConfig } from 'constants/loyalty.constants';
import { brandConfig } from 'constants/saudi-rewards.constants';
import { LoyaltyTierName } from 'types/api/account.types';

export const aspectRatio = (w: number, h: number) => {
  return {
    aspectRatio: `${w} / ${h}`,
    '@supports not (aspect-ratio: auto)': {
      position: 'relative',
      '&:before': {
        content: '""',
        float: 'left',
        paddingTop: `calc(100% * ${h / w})`,
      },
      '&:after': {
        display: 'block',
        content: '""',
        clear: 'both',
      },
    },
  };
};

export const cityCardHover = {
  '&:hover': {
    filter: 'brightness(0.9)',
    transition: 'filter 0.3s ease-in-out',
  },
  '&:active': {
    filter: 'brightness(0.8)',
    transition: 'filter 0.3s ease-in-out',
  },
};

export const eventListItemHover = {
  '&:hover': {
    '& > *:not(img)': {
      opacity: 0.7,
      transition: 'all 100ms linear',
    },
    '& > * > img': {
      transform: 'scale(1.1)',
      transition: 'transform 0.3s ease-in-out',
    },
    '& > *:first-of-type': {
      '&:before': {
        opacity: 0.7,
        transition: 'all 100ms linear',
        background:
          'linear-gradient(180deg, rgba(20, 19, 22, 0) 0%, #141316 100%)',
      },
    },
  },

  //when leaving hover state the image should transition to normal and not jump back instantly
  '& > * > img': {
    transform: 'scale(1)',
    transition: 'transform 0.3s ease-in-out',
  },
};

export const borderedCardHover = (theme: Theme) => ({
  transition: theme.transitions.create(['background-color', 'border-color']),
  alignItems: 'flex-start',
  flexDirection: 'row',
  justifyContent: 'space-between',
  color: 'text.primary',
  '&:hover, &:active': {
    color: 'unset',
  },
  '@media (hover: none)': {
    '&:active': {
      backgroundColor: 'grey.900',
    },
  },
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: 'grey.900',
    },
    '&:active': {
      borderColor: 'grey.500',
    },
  },
});

// This is a workaround for iOS devices and in-app browsers when working with modals.
// We prevent scrolling the page on iOS with the usePreventScroll hook.
// Same for in-app browsers or webviews inside apps (mdlbeast app/grip app).
// This causes the keyboard to cover the input fields in the modal.
// Only needed when there are input fields in the modal.
export const KeyboardPaddingBottom = (theme: Theme) => ({
  [theme.breakpoints.down('md')]: {
    paddingBottom: '420px',
  },
});

export const listScrollGradient = (size: number = 40) => ({
  '&::before': {
    content: '""',
    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, #000 80%)',
    position: 'absolute',
    top: -size,
    left: 0,
    right: 0,
    width: '100%',
    height: size,
  },
});

const sharedTextGradientStyles = {
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textFillColor: 'transparent',
};

export const loyaltyTextGradient = (tier: LoyaltyTierName) => {
  const loyaltyTier = tierConfig[tier];

  return {
    background: `linear-gradient(90deg, ${loyaltyTier.gradientColor.start} 0%, ${loyaltyTier.gradientColor.end} 100%)`,
    ...sharedTextGradientStyles,
  };
};

export const saudiRewardsTextGradient = {
  background: `linear-gradient(90deg, ${brandConfig.gradient.start} 0%, ${brandConfig.gradient.end} 100%)`,
  ...sharedTextGradientStyles,
};
